import React, { useMemo, useState } from 'react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  formatHistoryStatus,
  formatHistoryRollLabel,
  formatCompactPnl,
  formatDateTime,
  getPnlClassName,
  normalizeSizeDisplay,
  sortLegsByStrike,
} from './optionsHistoryUtils';
import { useOptionsLocale } from '../../context/OptionsLanguageContext';
import { formatExpirationDate, formatFeesDisplay, formatOpenedDate, formatOperationPnl } from './optionsOpenPositionsUtils';
import { formatNative } from './optionsDashboardUtils';
import { OptionsMonetaryText } from './OptionsMonetaryText';
import { OptionsLegGreeksGrid } from './OptionsLegGreeksGrid';
import { OptionsUnderlyingStrategyTitle } from './OptionsUnderlyingStrategyTitle';
import { CompactMetric, CompactPositionSummary, ActiveLegCard, ClosedLegCard, RollChainSummary } from './OptionsOpenPositionCard';
import { formatOperationDirectionalBiasLabel } from './optionsStrategyBiasUtils';
import GeniLoader from '../../components/GeniLoader';
import { formatLocalizedNumber } from './optionsLocaleFormatting';

export const OptionsHistoryOperationCard = ({ operation, index, selectedCurrency, campaign, detail, onExpand, loadingDetail }) => {
  const { t, formatMessage, isPortuguese } = useOptionsLocale();
  const [expanded, setExpanded] = useState(false);

  const pnl = formatOperationPnl({
    selectedCurrency,
    nativeValue: operation.net_pnl_base,
    usdValue: operation.net_pnl_usd,
    underlying: operation.underlying,
  });
  const fees = formatFeesDisplay({ selectedCurrency, nativeValue: operation.total_fees_base, usdValue: operation.total_fees_usd, underlying: operation.underlying });
  const expirationLabel = useMemo(() => formatExpirationDate(operation.expiration, isPortuguese ? 'pt' : 'en'), [operation.expiration, isPortuguese]);
  const compactLegs = useMemo(() => sortLegsByStrike(operation.legs_summary || []), [operation.legs_summary]);

  const handleToggle = async (value) => {
    const isOpen = Boolean(value);
    setExpanded(isOpen);
    if (isOpen && !detail) {
      await onExpand(operation.id);
    }
  };

  const orderedLegs = useMemo(() => sortLegsByStrike(detail?.legs || []), [detail]);
  const activeLegs = useMemo(() => orderedLegs.filter(leg => leg.status === 'OPEN'), [orderedLegs]);
  const closedLegs = useMemo(() => orderedLegs.filter(leg => leg.status !== 'OPEN'), [orderedLegs]);
  const rollHistory = operation.roll_history || [];
  const directionalBiasLabel = useMemo(() => formatOperationDirectionalBiasLabel(operation, t), [operation, t]);

  return (
    <Card className="border border-white/5 bg-zinc-950/40 backdrop-blur-md hover:bg-zinc-950/60 hover:border-white/10 transition-all duration-300 rounded-[24px] text-white shadow-none" data-testid={`options-history-operation-card-${index}`}>
      <CardContent className="p-6 sm:p-7">
        <Accordion type="single" collapsible value={expanded ? operation.id : ''} onValueChange={handleToggle}>
          <AccordionItem value={operation.id} className="border-none" data-testid={`options-history-operation-accordion-${index}`}>
            <div className="space-y-6">
              <OptionsUnderlyingStrategyTitle
                as="h2"
                underlying={operation.underlying}
                strategy={operation.strategy_label}
                iconSize={18}
                className="text-lg font-black font-heading text-white sm:text-xl"
                testId={`options-history-operation-strategy-${index}`}
                suffixNode={
                  <span className="flex flex-wrap items-center gap-2.5 normal-case" data-testid={`options-history-operation-badges-${index}`}>
                    <Badge className="border border-white/10 bg-white/5 text-white/60 text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-sm" data-testid={`options-history-operation-status-${index}`}>{formatHistoryStatus(operation.status, t)}</Badge>
                    {campaign?.roll_link_count > 0 ? (
                      <Badge className="border-[#00E676]/20 bg-[#00E676]/8 text-[#00E676] text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-sm" data-testid={`options-history-operation-rolled-${index}`}>{formatHistoryRollLabel(t)}</Badge>
                    ) : null}
                  </span>
                }
              />

              <div className="grid gap-x-6 gap-y-4 border-y border-white/5 py-4 sm:grid-cols-[minmax(190px,1.8fr)_repeat(4,minmax(88px,0.72fr))] sm:items-center" data-testid={`options-history-operation-summary-${index}`}>
                <CompactMetric label={isPortuguese ? 'Posição' : 'Position'} testId={`options-history-operation-position-${index}`}>
                  <CompactPositionSummary 
                    legs={compactLegs} 
                    underlying={operation.underlying} 
                    testId={`options-history-operation-position-${index}-value`} 
                    operationQuantity={operation.quantity}
                  />
                </CompactMetric>
                <CompactMetric label={t('history.opened')} value={formatOpenedDate(operation.opened_at)} testId={`options-history-operation-opened-${index}`} />
                <CompactMetric label={t('history.expiration')} value={expirationLabel} testId={`options-history-operation-expiration-${index}`} />
                <CompactMetric label={t('positions.netPnl')} value={pnl.primary} valueClassName={pnl.colorClassName} monetary testId={`options-history-operation-pnl-${index}`} />
                <CompactMetric label={t('dashboard.fees')} value={fees.primary} monetary testId={`options-history-operation-fees-${index}`} />
              </div>

              <div className="flex flex-col gap-3 text-xs text-white/55 sm:flex-row sm:items-center sm:justify-between" data-testid={`options-history-operation-secondary-${index}`}>
                <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
                  {campaign ? <span data-testid={`options-history-operation-campaign-${index}`}>{formatMessage(t('positions.campaignOperations'), { count: campaign.operation_count })}</span> : null}
                  {operation.directional_bias ? <><span className="text-white/25">•</span><span data-testid={`options-history-operation-directional-${index}`}>{directionalBiasLabel}</span></> : null}
                </div>
                <AccordionTrigger 
                  className="h-auto w-auto shrink-0 p-0 text-xs font-bold text-white/70 hover:text-[#00E676] transition-colors duration-200 no-underline hover:no-underline [&[data-state=open]>svg]:rotate-180" 
                  data-testid={`options-history-operation-expand-${index}`}
                >
                  <span className="flex items-center gap-1.5">
                    {expanded ? t('history.hideDetails') : t('history.viewDetails')}
                  </span>
                </AccordionTrigger>
              </div>
            </div>

            <AccordionContent className="pt-6" data-testid={`options-history-operation-details-${index}`}>
              {loadingDetail && !detail ? (
                <div className="rounded-[20px] border border-white/5 bg-zinc-950/20 px-4 py-8" data-testid={`options-history-operation-loading-${index}`}>
                  <div className="min-h-[120px]">
                    <GeniLoader size="md" />
                  </div>
                </div>
              ) : detail ? (
                <div className="space-y-6 rounded-[20px] border border-white/5 bg-zinc-950/20 p-5 sm:p-6 backdrop-blur-sm">

                  {/* 1. LEGS ATIVAS */}
                  {activeLegs.length > 0 ? (
                    <div className="space-y-3" data-testid={`options-history-operation-active-legs-${index}`}>
                      <p className="text-sm font-semibold uppercase tracking-[0.2em] text-white/45">
                        {isPortuguese ? 'LEGS ATIVAS' : 'ACTIVE LEGS'}
                      </p>
                      <div className="grid gap-3 lg:grid-cols-2">
                        {activeLegs.map((leg, legIdx) => (
                          <ActiveLegCard key={leg.id} leg={leg} selectedCurrency={selectedCurrency} positionIndex={index} legIndex={legIdx} />
                        ))}
                      </div>
                    </div>
                  ) : null}

                  {/* 2. ROLAGENS */}
                  {rollHistory.length > 0 ? (
                    <div className="space-y-3" data-testid={`options-history-operation-rolls-${index}`}>
                      <p className="text-sm font-semibold uppercase tracking-[0.2em] text-white/45">
                        {rollHistory.length > 1 ? (isPortuguese ? 'ROLAGENS' : 'ROLLS') : (isPortuguese ? 'ROLAGEM' : 'ROLL')}
                      </p>
                      {rollHistory.length > 1 ? <RollChainSummary rollHistory={rollHistory} /> : null}
                      <div className="grid gap-3 lg:grid-cols-2">
                        {rollHistory.map((entry, rollIdx) => {
                          const resultValue = Number(entry.realized_pnl_base ?? entry.realized_pnl_usd ?? 0);
                          let badgeLabel = '';
                          let badgeClassName = '';
                          if (resultValue > 0) {
                            badgeLabel = isPortuguese ? 'ROLAGEM COM LUCRO' : 'ROLLED WITH PROFIT';
                            badgeClassName = 'border-[#4ADE80]/20 bg-[#4ADE80]/12 text-[#BDFACD]';
                          } else if (resultValue < 0) {
                            badgeLabel = isPortuguese ? 'ROLAGEM COM PREJUÍZO' : 'ROLLED WITH LOSS';
                            badgeClassName = 'border-[#EF4444]/20 bg-[#EF4444]/12 text-[#FFD2D2]';
                          } else {
                            badgeLabel = isPortuguese ? 'ROLAGEM NEUTRA' : 'NEUTRAL ROLL';
                            badgeClassName = 'border-white/10 bg-white/[0.05] text-white';
                          }
                          const fromLabel = `${entry.from_option_type} ${formatLocalizedNumber(Number(entry.from_strike || 0), { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
                          const toLabel = `${entry.to_option_type} ${formatLocalizedNumber(Number(entry.to_strike || 0), { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
                          const resultDisplay = formatOperationPnl({ selectedCurrency, nativeValue: entry.realized_pnl_base, usdValue: entry.realized_pnl_usd, underlying: operation.underlying });
                          const rollFeesDisplay = formatFeesDisplay({ selectedCurrency, nativeValue: entry.fees_base, usdValue: entry.fees_usd, underlying: operation.underlying });
                          return (
                            <div key={rollIdx} className="rounded-2xl border border-white/10 bg-white/[0.02] p-4 space-y-3" data-testid={`options-history-operation-roll-card-${index}-${rollIdx}`}>
                              <div className="flex items-center justify-between gap-2">
                                <Badge className={`${badgeClassName} uppercase text-[10px] tracking-wider`}>
                                  {badgeLabel}
                                </Badge>
                                <span className="text-[11px] text-white/50">{formatOpenedDate(entry.closed_at)}</span>
                              </div>
                              <p className="text-base font-bold text-white tabular-nums">
                                {fromLabel} <span className="mx-1.5 text-white/35">→</span> {toLabel}
                              </p>
                              <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-white/60">
                                <span>{isPortuguese ? 'Resultado' : 'Result'}: <span className={`font-semibold ${resultDisplay.colorClassName}`}><OptionsMonetaryText value={resultDisplay.primary} iconSize={13} /></span></span>
                                <span>{isPortuguese ? 'Taxas' : 'Fees'}: <span className="font-semibold text-white/80"><OptionsMonetaryText value={rollFeesDisplay.primary} iconSize={13} /></span></span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ) : null}

                  {/* 3. LEGS REALIZADAS */}
                  {closedLegs.length > 0 ? (
                    <div className="space-y-3" data-testid={`options-history-operation-closed-legs-${index}`}>
                      <p className="text-sm font-semibold uppercase tracking-[0.2em] text-white/45">
                        {isPortuguese ? 'LEGS REALIZADAS' : 'REALIZED LEGS'}
                      </p>
                      <div className="grid gap-3 lg:grid-cols-2">
                        {closedLegs.map((leg, legIdx) => (
                          <ClosedLegCard key={leg.id} leg={leg} selectedCurrency={selectedCurrency} positionIndex={index} legIndex={legIdx} />
                        ))}
                      </div>
                    </div>
                  ) : null}

                </div>
              ) : null}
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </CardContent>
    </Card>
  );
};

export default OptionsHistoryOperationCard;